/**
 * Cover Art Selection Dialogs for Master Database
 * - Game Cover Art Dialog (IGDB + SteamGridDB)
 * - DLC Cover Art Dialog (IGDB + SteamGridDB)
 */
import { Loader2, Search, Check, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import SystemBarOverlay from "@/components/SystemBarOverlay";

/**
 * Game Cover Art Selection Dialog
 */
export function GameCoverArtDialog({
  open,
  onOpenChange,
  selectedGame,
  searchQuery,
  onSearchQueryChange,
  onSearch,
  loading,
  results,
  sourceFilter,
  onSourceFilterChange,
  selectedCover,
  onSelectCover,
  uploading,
  onApply,
  getFilteredResults,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-orange-400" />
            Get Cover Art
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Search IGDB and SteamGridDB for high-quality cover art
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 pt-4">
          {/* Search */}
          <div className="flex gap-2">
            <Input
              value={searchQuery}
              onChange={(e) => onSearchQueryChange(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onSearch()}
              placeholder="Search game name..."
              className="bg-black/50 border-white/10 flex-1"
              data-testid="cover-search-input"
            />
            <Button 
              onClick={onSearch} 
              disabled={loading}
              className="bg-orange-600 hover:bg-orange-500"
              data-testid="cover-search-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
          
          {/* Source Filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-white/50">Source:</span>
            <div className="flex gap-1">
              {["all", "igdb", "steamgriddb"].map((source) => (
                <Button
                  key={source}
                  size="sm"
                  variant={sourceFilter === source ? "default" : "ghost"}
                  onClick={() => onSourceFilterChange(source)}
                  className={`h-7 px-3 text-xs ${
                    sourceFilter === source 
                      ? "bg-orange-600 hover:bg-orange-500" 
                      : "text-white/60 hover:text-white"
                  }`}
                  data-testid={`cover-filter-${source}`}
                >
                  {source === "all" ? "All Sources" : source === "igdb" ? "IGDB" : "SteamGridDB"}
                </Button>
              ))}
            </div>
            {results.combined?.length > 0 && (
              <span className="text-xs text-white/40 ml-auto">
                {results.igdb?.length || 0} IGDB • {results.steamgriddb?.length || 0} SteamGridDB
              </span>
            )}
          </div>
          
          {/* Current cover */}
          {selectedGame?.boxArtUrl && (
            <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
              <img 
                src={selectedGame.boxArtUrl} 
                alt="Current" 
                className="w-16 h-24 object-contain rounded bg-black/50"
              />
              <div>
                <p className="text-xs text-white/50">Current Box Art</p>
                <p className="text-sm text-white">{selectedGame.name}</p>
              </div>
            </div>
          )}
          
          {/* Results */}
          {getFilteredResults().length > 0 && (
            <div className="space-y-4">
              {getFilteredResults().map((game) => (
                <div key={game.source_id} className="border border-white/10 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-3">
                    <p className="font-medium text-white">{game.name}</p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase ${
                      game.source === "igdb" 
                        ? "bg-purple-500/20 text-purple-300" 
                        : "bg-blue-500/20 text-blue-300"
                    }`}>
                      {game.source}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {game.covers.map((cover, idx) => (
                      <div 
                        key={idx}
                        onClick={() => onSelectCover(cover)}
                        className={`cursor-pointer rounded-lg p-2 transition-all ${
                          selectedCover?.url === cover.url 
                            ? "bg-orange-500/20 border-2 border-orange-500" 
                            : "bg-white/5 border border-white/10 hover:bg-white/10"
                        }`}
                        data-testid={`cover-option-${game.source_id}-${idx}`}
                      >
                        <div className="aspect-[2/3] bg-black/50 rounded overflow-hidden mb-2">
                          <img 
                            src={cover.preview_url} 
                            alt={`${cover.type} ${cover.size}`}
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <div className="text-center">
                          <p className="text-[10px] text-white/50 uppercase">{cover.type}</p>
                          <p className="text-xs text-white">{cover.dimensions}</p>
                          {cover.author && (
                            <p className="text-[9px] text-white/30 truncate">by {cover.author}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Selected preview */}
          {selectedCover && (
            <div className="border border-orange-500/30 bg-orange-500/10 rounded-lg p-4">
              <p className="text-sm text-orange-300 mb-3">Selected Cover Preview</p>
              <div className="flex items-start gap-4">
                <div className="w-32 flex flex-col rounded overflow-hidden bg-black/50">
                  {selectedGame?.showSystemBar && (
                    <SystemBarOverlay 
                      platform={selectedGame.platforms?.[0]} 
                      system={selectedGame.systems?.[0]}
                      override={selectedGame.systemBarOverride}
                      show={true}
                    />
                  )}
                  <div className="aspect-[2/3]">
                    <img 
                      src={selectedCover.preview_url} 
                      alt="Selected"
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-xs text-white/50">Source: <span className={`${
                    selectedCover.source === "igdb" ? "text-purple-300" : "text-blue-300"
                  }`}>{selectedCover.source?.toUpperCase()}</span></p>
                  <p className="text-xs text-white/50">Type: <span className="text-white">{selectedCover.type}</span></p>
                  <p className="text-xs text-white/50">Size: <span className="text-white">{selectedCover.dimensions}</span></p>
                  {selectedCover.author && (
                    <p className="text-xs text-white/50">Author: <span className="text-white">{selectedCover.author}</span></p>
                  )}
                  <p className="text-[10px] text-white/30 mt-2 break-all">{selectedCover.url}</p>
                  
                  {/* System bar indicator */}
                  {selectedGame?.showSystemBar && (
                    <div className="mt-3 p-2 bg-green-500/10 border border-green-500/30 rounded text-xs">
                      <span className="text-green-400">System bar enabled</span>
                      <span className="text-white/50 ml-1">
                        ({selectedGame.systemBarOverride || selectedGame.systems?.[0] || selectedGame.platforms?.[0] || 'Auto'})
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="flex justify-end gap-3 pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-white/20"
            data-testid="cover-dialog-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={onApply}
            disabled={!selectedCover || uploading}
            className="bg-orange-600 hover:bg-orange-500"
            data-testid="cover-dialog-apply"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Apply Cover
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/**
 * DLC Cover Art Selection Dialog
 */
export function DlcCoverArtDialog({
  open,
  onOpenChange,
  dlcForSearch,
  searchQuery,
  onSearchQueryChange,
  onSearch,
  loading,
  results,
  sourceFilter,
  onSourceFilterChange,
  selectedCover,
  onSelectCover,
  uploading,
  onApply,
  getFilteredResults,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-blue-400" />
            Get DLC Cover Art
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Search IGDB and SteamGridDB for DLC cover art: {dlcForSearch?.name}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 pt-4">
          {/* Search */}
          <div className="flex gap-2">
            <Input
              value={searchQuery}
              onChange={(e) => onSearchQueryChange(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onSearch()}
              placeholder="Search DLC name..."
              className="bg-black/50 border-white/10 flex-1"
              data-testid="dlc-cover-search-input"
            />
            <Button 
              onClick={onSearch} 
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-500"
              data-testid="dlc-cover-search-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
          
          {/* Source Filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-white/50">Source:</span>
            <div className="flex gap-1">
              {["all", "igdb", "steamgriddb"].map((source) => (
                <Button
                  key={source}
                  size="sm"
                  variant={sourceFilter === source ? "default" : "ghost"}
                  onClick={() => onSourceFilterChange(source)}
                  className={`h-7 px-3 text-xs ${
                    sourceFilter === source 
                      ? "bg-blue-600 hover:bg-blue-500" 
                      : "text-white/60 hover:text-white"
                  }`}
                  data-testid={`dlc-cover-filter-${source}`}
                >
                  {source === "all" ? "All Sources" : source === "igdb" ? "IGDB" : "SteamGridDB"}
                </Button>
              ))}
            </div>
            {results.combined?.length > 0 && (
              <span className="text-xs text-white/40 ml-auto">
                {results.igdb?.length || 0} IGDB • {results.steamgriddb?.length || 0} SteamGridDB
              </span>
            )}
          </div>
          
          {/* Current DLC cover */}
          {dlcForSearch?.boxArtUrl && (
            <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
              <img 
                src={dlcForSearch.boxArtUrl} 
                alt="Current" 
                className="w-16 h-24 object-contain rounded bg-black/50"
              />
              <div>
                <p className="text-xs text-white/50">Current DLC Box Art</p>
                <p className="text-sm text-white">{dlcForSearch.name}</p>
              </div>
            </div>
          )}
          
          {/* Results */}
          {getFilteredResults().length > 0 && (
            <div className="space-y-4">
              {getFilteredResults().map((game) => (
                <div key={game.source_id} className="border border-white/10 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-3">
                    <p className="font-medium text-white">{game.name}</p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase ${
                      game.source === "igdb" 
                        ? "bg-purple-500/20 text-purple-300" 
                        : "bg-blue-500/20 text-blue-300"
                    }`}>
                      {game.source}
                    </span>
                  </div>
                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                    {game.covers.map((cover, idx) => (
                      <div
                        key={idx}
                        onClick={() => onSelectCover(cover)}
                        className={`relative cursor-pointer rounded overflow-hidden aspect-[3/4] bg-black/50 ${
                          selectedCover?.url === cover.url 
                            ? "ring-2 ring-blue-500" 
                            : "hover:ring-1 hover:ring-white/30"
                        }`}
                      >
                        <img
                          src={cover.thumb || cover.url}
                          alt={cover.type}
                          className="w-full h-full object-contain"
                          loading="lazy"
                        />
                        {selectedCover?.url === cover.url && (
                          <div className="absolute inset-0 bg-blue-500/20 flex items-center justify-center">
                            <Check className="w-6 h-6 text-blue-400" />
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-black/80 px-1 py-0.5">
                          <p className="text-[8px] text-white/70 truncate">{cover.type}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Selected cover preview */}
          {selectedCover && (
            <div className="p-4 border border-blue-500/30 rounded-lg bg-blue-500/5">
              <p className="text-sm font-medium text-blue-400 mb-2">Selected Cover:</p>
              <div className="flex gap-4">
                <div className="w-24 h-32 bg-black/50 rounded overflow-hidden flex-shrink-0">
                  <img
                    src={selectedCover.url}
                    alt="Selected"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-white/50">Source: <span className={`${
                    selectedCover.source === "igdb" ? "text-purple-300" : "text-blue-300"
                  }`}>{selectedCover.source?.toUpperCase()}</span></p>
                  <p className="text-xs text-white/50">Type: <span className="text-white">{selectedCover.type}</span></p>
                  <p className="text-xs text-white/50">Size: <span className="text-white">{selectedCover.dimensions}</span></p>
                  {selectedCover.author && (
                    <p className="text-xs text-white/50">Author: <span className="text-white">{selectedCover.author}</span></p>
                  )}
                  <p className="text-[10px] text-white/30 mt-2 break-all">{selectedCover.url}</p>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="flex justify-end gap-3 pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-white/20"
            data-testid="dlc-cover-dialog-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={onApply}
            disabled={!selectedCover || uploading}
            className="bg-blue-600 hover:bg-blue-500"
            data-testid="dlc-cover-dialog-apply"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Apply DLC Cover
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
